import { Canvas, useFrame } from '@react-three/fiber';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useImperativeHandle, useRef } from 'react';
import { useIntersectionObserver } from '../../../../hooks/useIntersectionObserver';
import type { AnimalType } from '../ServicePowers.data';
import Scene from './scene/Scene';
import type { SceneWebglRef } from './scene/Scene.data';
import type { ServicePowerWebglProps, ServicePowerWebglRef } from './ServicePowerWebgl.data';
import styles from './ServicePowerWebgl.module.scss';
import { useDocumentVisibilityChange } from '../../../../hooks/useDocumentVisibilityChange';

function DisableRender(): null {
  useFrame(() => null, 1000);
  return null;
}

export default function ServicePowerWebgl({
  slides,
  className,
  ref,
}: ServicePowerWebglProps & { ref?: Ref<ServicePowerWebglRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<ServicePowerWebglRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const sceneWebglRef = useRef<SceneWebglRef>(null);
  const currentSlide = useRef(0);

  const setProgress = useCallback((value: number) => {
    // console.log(value, 'setProgress');
    if (sceneWebglRef?.current) sceneWebglRef.current.setProgress(value);
  }, []);

  const setCurrentSlide = useCallback((slide: number) => {
    // console.log(value, 'setProgress');
    currentSlide.current = slide;
    if (sceneWebglRef?.current) sceneWebglRef.current.setCurrentSlide(slide);
  }, []);

  const setProgressData = useCallback(
    (
      progressData: Array<{
        id: AnimalType;
        progress: number;
      }>,
    ) => {
      if (sceneWebglRef?.current) sceneWebglRef.current.setProgressData(progressData);
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      setCurrentSlide,
      setProgressData,
      elementRef,
    }),
    [setProgressData, elementRef, setProgress, setCurrentSlide],
  );

  const intersectionRef = useRef(null);
  const isVisibleInViewport = useIntersectionObserver(intersectionRef, {
    threshold: [0, 1],
  });
  const [isDocumentHidden] = useDocumentVisibilityChange();

  return (
    <div ref={elementRef} className={classNames(styles.servicePowerWebgl, className)}>
      <div
        className={classNames(styles.isInViewWrap, className)}
        ref={intersectionRef as RefObject<HTMLDivElement | null>}
      >
        {/* <Canvas shadows dpr={getMinMaxDpr()}> */}

        <Canvas shadows>
          {isDocumentHidden || (!isVisibleInViewport && <DisableRender />)}

          <Scene ref={sceneWebglRef} slides={slides} />
        </Canvas>
      </div>
    </div>
  );
}
